import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import Card from 'components/Layout/Card';
import Table from 'components/Layout/Table';
import Button from 'components/form/Button';
import SeachInput from 'components/form/SeachInput';

import { fetchAllTrackingCertificate, fetchTrackingAnalytics } from 'store/slices';
import {
  getUserType,
  getDataFromLocalStorage,
  objectToFormData,
  paymentColor,
} from 'utils/helpers';
import { icons, limit } from 'utils/constants';
import usePermission from 'hooks/usePermission';
import Loader from 'components/Layout/Loader';

const EventCertificatesUserList = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { eventID } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [tableList, setTableList] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [timer, setTimer] = useState(null);

  const [searchPayload, setSearchPayload] = useState({
    global_filter: '',
  });

  const [filterData, setFilterData] = useState({
    total: 0,
    offset: 0,
    limit: limit,
  });
  const { viewAccess, createAccess, updateAccess, deleteAccess } = usePermission('Tracking');

  const [expandedIndex, setExpandedIndex] = useState(null);
  const [analyticsData, setAnalyticsData] = useState({});
  const [analyticsLoading, setAnalyticsLoading] = useState(true);



  const getTrackingAnalytics = async (eventID) => {
    const response = await dispatch(fetchTrackingAnalytics(eventID));
    if (response?.data) {
      setAnalyticsData(response?.data || {});
    }
    setAnalyticsLoading(false);
  };

  // Fetch API
  const getAllTrackingCertificate = async (obj) => {
    let forData = objectToFormData(obj);
    const response = await dispatch(fetchAllTrackingCertificate(forData));
    setTableList(response?.data || []);
    setFilterData({
      ...obj,
      total: response?.data?.result_count || 0,
    });
    setIsLoading(false);
  };

  // Initial Fetch
  useEffect(() => {
    const userType = getDataFromLocalStorage('user_type');
    if (['0', '1', '6', '7'].includes(userType)) {
      getAllTrackingCertificate({ ...filterData, event_id: eventID });
      getTrackingAnalytics(eventID)
    } else {
      navigate('/');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventID]);

  // Debounced search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchText(value);
    clearTimeout(timer);

    const newTimer = setTimeout(() => {
      const updatedSearchPayload = {
        ...searchPayload,
        global_filter: value,
        event_id: eventID,
      };

      const updatedFilterData = {
        ...filterData,
        offset: 0,
        ...updatedSearchPayload,
      };

      setSearchPayload(updatedSearchPayload);
      setFilterData(updatedFilterData);
      getAllTrackingCertificate(updatedFilterData);
    }, 800);

    setTimer(newTimer);
  };

  // Pagination change
  const handelChangePagination = (offset) => {
    const newData = {
      ...filterData,
      offset,
      ...searchPayload,
      event_id: eventID,
    };
    getAllTrackingCertificate(newData);
  };

  // Filter change from table component
  const handelChangeSearch = (searchData) => {
    const newData = {
      ...filterData,
      ...searchData,
      offset: 0,
      event_id: eventID,
    };
    setSearchPayload(searchData);
    setFilterData(newData);
    getAllTrackingCertificate(newData);
  };

  // Table header
  const header = [
    { isSearch: false, searchInputName: '', title: 'Name' },
    { isSearch: false, searchInputName: '', title: 'Email Id' },
    { isSearch: false, searchInputName: '', title: 'Phone No' },
    { isSearch: false, searchInputName: '', title: 'Certificate type' },
    { isSearch: false, searchInputName: '', title: 'Added By' },
    { isSearch: false, searchInputName: '', title: 'Status' },
    { isSearch: false, title: 'Action' },
  ];

  // Table row data
  const rowData = [];
  tableList?.members?.forEach((elem) => {
    let obj = [
      { value: <div className="color-48c0">{elem?.client_name || '-'}</div> },
      { value: elem?.email_id || '-' },
      { value: elem?.phone_number || '-' },
      { value: elem?.certificate_type || '-' },
      { value: elem?.added_by || '-' },
      {
        value: (
          <div className={paymentColor(elem?.status)}>
            {elem?.status || '-'}
          </div>
        ),
      },
      {
        value: (
          <span className="action-icon-buttons">
            {viewAccess === '1' && (
              <Button
                btnStyle="light-outline"
                icon={<i className="bi bi-eye" />}
                className="me-2"
                onClick={() => {
                  const queryParams = new URLSearchParams();
                  // Only append if values exist
                  if (elem?.type) queryParams.append('type', elem.type);
                  if (elem?.request_id)
                    queryParams.append('request_id', elem.request_id);
                  if (elem?.user_type)
                    queryParams.append('user_type', elem.user_type);
                  if (elem?.user_type_category)
                    queryParams.append(
                      'user_type_category',
                      elem?.user_type_category
                    );

                  navigate(
                    `/${getUserType()}/tracking/certificate-management/event-certificates/${eventID}/${elem?.id
                    }?${queryParams.toString()}`
                  );
                }}
                isSquare
              />
            )}
          </span>
        ),
      },
    ];
    rowData.push({ data: obj });
  });


  const renderSection = (key, title, index, colPrefix) => {
    const data = analyticsData[key];
    const isOpen = expandedIndex === `${colPrefix}-${index}`;
    return (
      <div key={key}>
        <div className="d-flex align-items-center gap-2 cmb-17">
          <div className="text-14-600-18 color-new-car text-truncate fw-semibold">{title}</div>
          <div className="text-14-600-18 color-new-car fw-semibold">:{' '}{data?.total || 0}</div>
          <div
            className="color-new-car ms-2 pointer"
            onClick={() =>
              setExpandedIndex(isOpen ? null : `${colPrefix}-${index}`)
            }
            style={{ cursor: "pointer" }}
          >
            <i
              className={`bi ${isOpen ? "bi-chevron-up" : "bi-chevron-down"
                } fs-6 color-new-car`}
            ></i>
          </div>
        </div>

        {isOpen && (
          <div className="mb-3 mt-2">
            <div className="text-14-300-18 cmb-12">
              <span className="color-c05c">●</span> Certificate Sent :{" "}
              <span className='text-14-500'>{data?.sent || 0}</span>
            </div>
            <div className="text-14-300-18 cmb-12">
              <span className="color-4646">●</span> Certificate Not Sent :{" "}
              <span className='text-14-500'>{data?.not_sent || 0}</span>
            </div>
            <div className="text-14-300-18 cmb-12">
              <span className="color-a35b">●</span> Certificate Sent &
              Downloaded :{" "}<span className='text-14-500'>{data?.sent_downloaded || 0}</span>
            </div>
            <div className="text-14-300-18">
              <span className="color-fd6b">●</span> Certificate Sent but Not
              Downloaded :{" "}<span className='text-14-500'>{data?.sent_not_downloaded || 0}</span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Card className="cp-10">
        {analyticsLoading ?
          <>
            <div className='d-flex justify-content-center cp-20'>
              <Loader size="md" />
            </div>
          </> :
          <>
            <div
              className="d-flex p-2 align-items-center border-bottom"
              onClick={() => navigate(-1)}
            >
              <img
                src={icons?.leftArrow}
                alt="back"
                className="h-21 me-2 pointer"
              />
              <div className="text-16-500-20 color-title-navy">
                {tableList?.event_name}
              </div>
            </div>



            <div className="row cmt-20 gy-md-3 cps-14">
              {/* Left Column */}
              <div className="col-12 col-md-6 col-lg-5">
                {renderSection("participants", "Total Number of Participants", 0, "L")}
                {renderSection("authors", "Total Number of Authors", 1, "L")}
                {renderSection("co_authors", "Total Number of Co-Authors", 2, "L")}
              </div>

              {/* Right Column */}
              <div className="col-12 col-md-6 col-lg-7">
                {renderSection("listeners", "Total Number of Listeners", 0, "R")}
                {renderSection("speakers", "Total Number of Speakers", 1, "R")}
                {renderSection(
                  "committee_members",
                  "Total Number of Committee Members",
                  2,
                  "R"
                )}
              </div>
            </div>
          </>}
      </Card>



      <Card className="cps-20 cpe-20 cpb-20 cmt-10">
        {/* Header section with back and search */}
        <div className="d-flex justify-content-end align-items-center cpt-15 cpb-15">
          <div style={{ width: '150px' }} className="me-3">
            <SeachInput
              placeholder="Search"
              value={searchText}
              onChange={handleSearch}
              className="small-text-input"
              iconClassName="small-search-icon"
            />
          </div>
        </div>

        {/* Table */}
        <Table
          isLoading={isLoading || analyticsLoading}
          header={header}
          rowData={rowData}
          filterData={filterData}
          searchPayload={searchPayload}
          searchInputChange={handelChangeSearch}
          changeOffset={handelChangePagination}
          maxHeight="60vh"
        />
      </Card>
    </>
  );
};

export default EventCertificatesUserList;
